import { useState, useRef, useEffect } from "react";
import "../styles/ChatAssistant.css";

const API = `${process.env.REACT_APP_API_URL}/api` || "http://localhost:8000/api";

const QUICK_PROMPTS = [
  { label: "🏠 List a property", text: "How do I list a property on GeoState?" },
  { label: "💰 Calculate EMI", text: "How does the EMI calculator work?" },
  { label: "📍 Best areas in Pune", text: "What are the best areas to rent in Pune?" },
  { label: "📄 Renting checklist", text: "What should I check before renting a flat?" },
];

const TypingIndicator = () => (
  <div className="geobot-typing">
    <div className="geobot-typing-avatar">G</div>
    <div className="geobot-typing-dots">
      <span /><span /><span />
    </div>
  </div>
);

const Message = ({ msg }) => {
  const isUser = msg.role === "user";
  return (
    <div className={`geobot-message ${isUser ? "geobot-message--user" : "geobot-message--bot"}`}>
      {!isUser && <div className="geobot-msg-avatar">G</div>}
      <div className="geobot-msg-bubble">
        <p>{msg.content}</p>
        <span className="geobot-msg-time">{msg.time}</span>
      </div>
    </div>
  );
};

export default function ChatAssistant() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const getTime = () =>
    new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

  // Greeting on first open
  useEffect(() => {
    if (open && !hasGreeted) {
      setHasGreeted(true);
      setTimeout(() => {
        setMessages([
          {
            role: "assistant",
            content:
              "Hi! I'm GeoBot 👋 Your GeoState assistant. I can help you with property listings, mortgage calculations, real estate advice, and navigating the platform. What can I help you with?",
            time: getTime(),
          },
        ]);
      }, 400);
    }
  }, [open, hasGreeted]);

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  // Focus input when opened
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 300);
  }, [open]);

  const sendMessage = async (text) => {
    const userText = text || input.trim();
    if (!userText || typing) return;

    const userMsg = { role: "user", content: userText, time: getTime() };
    const updatedMessages = [...messages, userMsg];

    setMessages(updatedMessages);
    setInput("");
    setTyping(true);

    // Build history to send (exclude time field)
    const history = updatedMessages.slice(0, -1).map((m) => ({
      role: m.role,
      content: m.content,
    }));

    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userText, history }),
      });

      const data = await res.json();
      setTyping(false);

      const botMsg = {
        role: "assistant",
        content: data.success
          ? data.reply
          : "Sorry, I'm having trouble connecting. Please try again.",
        time: getTime(),
      };

      setMessages((prev) => [...prev, botMsg]);
    } catch {
      setTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Something went wrong. Please try again in a moment.",
          time: getTime(),
        },
      ]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setHasGreeted(false);
    setTimeout(() => {
      setMessages([
        {
          role: "assistant",
          content: "Chat cleared. How can I help you?",
          time: getTime(),
        },
      ]);
      setHasGreeted(true);
    }, 200);
  };

  return (
    <>
      {/* Floating Button */}
      <button
        className={`geobot-fab ${open ? "geobot-fab--active" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-label="Open GeoBot assistant"
      >
        <div className="geobot-fab-inner">
          {open ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              <circle cx="9" cy="10" r="1" fill="currentColor" />
              <circle cx="12" cy="10" r="1" fill="currentColor" />
              <circle cx="15" cy="10" r="1" fill="currentColor" />
            </svg>
          )}
        </div>
        {!open && <div className="geobot-fab-pulse" />}
        {!open && <div className="geobot-fab-label">GeoBot</div>}
      </button>

      {/* Chat Panel */}
      <div className={`geobot-panel ${open ? "geobot-panel--open" : ""}`}>
        {/* Header */}
        <div className="geobot-header">
          <div className="geobot-header-info">
            <div className="geobot-header-avatar">G</div>
            <div>
              <p className="geobot-header-name">GeoBot</p>
              <p className="geobot-header-status">
                <span className="geobot-status-dot" />
                AI Assistant · GeoState
              </p>
            </div>
          </div>
          <button className="geobot-clear-btn" onClick={clearChat} title="Clear chat">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14H6L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4h6v2" />
            </svg>
          </button>
        </div>

        {/* Messages */}
        <div className="geobot-messages">
          {messages.length === 0 && !typing && (
            <div className="geobot-empty">
              <div className="geobot-empty-icon">💬</div>
              <p>Ask me anything about GeoState or real estate in India.</p>
            </div>
          )}

          {messages.map((msg, i) => (
            <Message key={i} msg={msg} />
          ))}

          {typing && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Prompts — only show if no messages yet */}
        {messages.length <= 1 && !typing && (
          <div className="geobot-quick-prompts">
            {QUICK_PROMPTS.map((q) => (
              <button
                key={q.label}
                className="geobot-quick-btn"
                onClick={() => sendMessage(q.text)}
              >
                {q.label}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="geobot-input-area">
          <input
            ref={inputRef}
            type="text"
            className="geobot-input"
            placeholder="Ask GeoBot..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={typing}
            maxLength={500}
          />
          <button
            className="geobot-send-btn"
            onClick={() => sendMessage()}
            disabled={!input.trim() || typing}
            aria-label="Send message"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>

        <p className="geobot-footer">Powered by Groq · GeoState AI</p>
      </div>
    </>
  );
}